import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ReasonDialogComponent } from '../../dialogs/reason-dialog/reason-dialog.component';
import { TrackedStatus } from '../../shared/time-tracking.service';
import { RegistrationWeekService } from '../registration-week.service';

@Component({
  selector: 'bst-registration-approval-strip',
  templateUrl: './approval-strip.component.html',
  styleUrls: ['./approval-strip.component.scss'],
  standalone: false,
})
export class ApprovalStripComponent {
  // Hours binds its live grid totals here; Km leaves it unbound (facade.load() sets it).
  @Input() set hasHours(v: boolean) { if (v != null) { this.week.hasHours = v; } }
  @Output() changed = new EventEmitter<void>();
  TrackedStatus = TrackedStatus;
  busy = false;

  constructor(public week: RegistrationWeekService, private dialog: NbDialogService, private translate: TranslateService) {}

  get statusKey(): string {
    switch (this.week.request?.status) {
      case TrackedStatus.Submitted: return 'registration.status.submitted';
      case TrackedStatus.Approved: return 'registration.status.approved';
      case TrackedStatus.Rejected: return 'registration.status.rejected';
      default: return 'registration.status.draft';
    }
  }

  // Most recent reject not superseded by submit/resubmit/approve/recall/reopen.
  // A later draft_edit does not clear it (US8, moved verbatim from TimesheetComponent).
  get lastRejection(): {reason: string, actor: any, at: string} | null {
    const clearing = new Set(['submit', 'resubmit', 'approve', 'recall', 'reopen']);
    let rej = null;
    for (const e of this.week.request?.events || []) {
      if (e.event_type === 'reject') { rej = e; } else if (clearing.has(e.event_type)) { rej = null; }
    }
    return rej ? {reason: rej.reason, actor: rej.actor, at: rej.created_at} : null;
  }

  get reviewerNames(): string {
    const seen = new Set<number>();
    return (this.week.request?.assignees || [])
      .filter(a => a.status === 1 && a.User_id != null && !seen.has(a.User_id) && seen.add(a.User_id))
      .map(a => [a.user?.name_first, a.user?.name_last].filter(Boolean).join(' '))
      .filter(Boolean)
      .join(', ');
  }

  get decidedByName(): string {
    const d = this.week.request?.decided_by;
    return d ? [d.name_first, d.name_last].filter(Boolean).join(' ') : '';
  }

  submit() { return this.run(() => this.week.submit()); }
  recall() { return this.run(() => this.week.recall()); }

  async reopen() {
    if (!this.week.canReopen) { return; }
    const reason = await new Promise<string | null>(resolve => this.dialog.open(ReasonDialogComponent, {
      context: {
        title: 'registration.reopen_title',
        subtitle: this.translate.instant('registration.reopen_subtitle'),
        placeholder: 'registration.reopen_placeholder',
        confirmLabel: 'registration.reopen',
        status: 'primary',
      },
    }).onClose.subscribe((r: string | null) => resolve(r ?? null)));
    if (reason) { await this.run(() => this.week.reopen(reason)); }
  }

  private async run(action: () => Promise<void>) {
    if (this.busy) { return; }
    this.busy = true;
    try { await action(); this.changed.emit(); } finally { this.busy = false; }
  }
}
